import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  InputAdornment,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Radio,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Import the new independent API functions
import {
  getRoutesByOrgIdIndependent,
  getStopsByOrgIdIndependent,
  getOrganizationById,
  fetchInitialLocations,
} from "./mobileApis";

import { CHARGING_STATION_RADIUS_METERS, isChargingStation, haversineKm, CHARGING_STATION_COORDS, isInactiveOrganization } from "./mobileScreenUtils";

import "./MobileStopScreen.css";
import { routeAllowsJourney } from "./journeyUtils";

import Org_Logo from "../../../images/navbar/bial_logo.jpg";
import Bus_Background from "../../../images/mobileStop/BusBackground.png";
import Info_Banner from "../../../images/mobileStop/info_banner.svg";
import Change_Icon from "../../../images/mobileStop/interchange_icon.svg";
import LeavingStartIcon from "../../../images/mobileStop/leaving_icon.svg";
import GoingStartIcon from "../../../images/mobileStop/going_icon.svg";
import Arrow_Down from "../../../images/mobileStop/arrow_down.svg";

// CHARGING STATION FILTER CONSTANTS


// Helper: Calculate distance between two points (Haversine formula)
const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const distKm = haversineKm({ lat: lat1, lng: lon1 }, { lat: lat2, lng: lon2 });
  return (distKm || 0) * 1000;
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getStopLatitude = (stop) => toNumber(stop?.latitude ?? stop?.lat);

const getStopLongitude = (stop) => toNumber(stop?.longitude ?? stop?.lng);

const areCoordinatesClose = (a, b) => {
  const aLat = getStopLatitude(a);
  const aLng = getStopLongitude(a);
  const bLat = getStopLatitude(b);
  const bLng = getStopLongitude(b);

  if (aLat === null || aLng === null || bLat === null || bLng === null) {
    return false;
  }

  return Math.abs(aLat - bLat) < 0.0001 && Math.abs(aLng - bLng) < 0.0001;
};

const isSameStop = (a, b) => {
  if (!a || !b) return false;
  if (a.id != null && b.id != null && String(a.id) === String(b.id)) {
    return true;
  }
  if (a.stopId != null && b.stopId != null && String(a.stopId) === String(b.stopId)) {
    return true;
  }
  if (a.stopId != null && b.id != null && String(a.stopId) === String(b.id)) {
    return true;
  }
  if (a.id != null && b.stopId != null && String(a.id) === String(b.stopId)) {
    return true;
  }
  return areCoordinatesClose(a, b);
};

const buildUniqueStops = (list) => {
  const unique = [];

  list.forEach((stop) => {
    if (!stop) return;
    const exists = unique.some((item) => isSameStop(item, stop));
    if (!exists) unique.push(stop);
  });

  return unique;
};

const buildRouteStopSequences = (routes = [], organizationStops = []) =>
  routes
    .map((route) => {
      const coordinates = Array.isArray(route.routeCoordinates)
        ? route.routeCoordinates
            .map((coordinate, index) => ({ ...coordinate, fallbackOrder: index }))
            .sort(
              (a, b) =>
                toNumber(a.order ?? a.fallbackOrder) -
                toNumber(b.order ?? b.fallbackOrder),
            )
        : [];

      const routeStops = coordinates
        .map((coordinate) => {
          const matchedStop = organizationStops.find((stop) => {
            if (
              coordinate.stopId != null &&
              stop.id != null &&
              String(coordinate.stopId) === String(stop.id)
            ) {
              return true;
            }
            return areCoordinatesClose(stop, coordinate);
          });

          if (!matchedStop && !coordinate.stopId && !coordinate.isStop) {
            return null;
          }

          return {
            ...(matchedStop || {}),
            id: matchedStop?.id ?? coordinate.stopId ?? coordinate.id,
            stopId: matchedStop?.id ?? coordinate.stopId ?? coordinate.id,
            name:
              matchedStop?.name ||
              coordinate.stopName ||
              coordinate.name ||
              "Unnamed Stop",
            latitude: getStopLatitude(matchedStop || coordinate),
            longitude: getStopLongitude(matchedStop || coordinate),
          };
        })
        .filter((stop) => stop?.latitude !== null && stop?.longitude !== null);

      return {
        routeId: route.id ?? route.routeId,
        stops: buildUniqueStops(routeStops),
      };
    })
    .filter((route) => route.stops.length > 0);

const getRelatedStopsForSelection = (routeStopSequences, selectedStop) => {
  if (!selectedStop || routeStopSequences.length === 0) return [];

  const relatedStops = routeStopSequences.flatMap((route) => {
    const hasSelectedStop = route.stops.some((stop) => isSameStop(stop, selectedStop));
    return hasSelectedStop ? route.stops : [];
  });

  return buildUniqueStops(relatedStops).filter(
    (stop) => !isSameStop(stop, selectedStop),
  );
};
// Helper: Filter vehicles near charging stations
const filterVehiclesNearChargingStations = (routesWithVehicles, stops, liveData) => {
  // Get all charging station coordinates
  const chargingStations = stops
    .filter(stop => isChargingStation(stop.name))
    .map(stop => ({
      name: stop.name,
      latitude: stop.latitude || stop.lat,
      longitude: stop.longitude || stop.lng,
    }))
    .filter(station => station.latitude && station.longitude);

  // Add hardcoded charging station
  chargingStations.push({
      name: "Charging Station (Fixed)",
      latitude: CHARGING_STATION_COORDS.lat,
      longitude: CHARGING_STATION_COORDS.lng
  });

  if (chargingStations.length === 0) {
    console.log("No charging stations found");
    return routesWithVehicles; // No filtering needed
  }

  console.log(`Found ${chargingStations.length} charging stations:`, chargingStations);

  // Filter routes and their vehicles
  const filteredRoutes = routesWithVehicles.map(route => {
    const filteredVehicles = (route.vehicles || []).filter(vehicle => {
      const deviceId =
        vehicle.deviceId ||
        vehicle.device_id ||
        (vehicle.device && vehicle.device.id) ||
        vehicle.id;

      if (!deviceId) return true; // Keep if no device ID

      // Get live location
      const live = liveData?.[deviceId];
      if (!live?.latitude || !live?.longitude) {
        return true; // Keep if no live data (benefit of doubt)
      }

      // Check distance to each charging station
      const nearChargingStation = chargingStations.some(station => {
        const distance = haversineDistance(
          live.latitude,
          live.longitude,
          station.latitude,
          station.longitude
        );

        if (distance <= CHARGING_STATION_RADIUS_METERS) {
          console.log(
            `🚫 Vehicle ${vehicle.name || deviceId} filtered: ` +
            `${distance.toFixed(1)}m from ${station.name}`
          );
          return true;
        }
        return false;
      });

      return !nearChargingStation; // Keep only if NOT near charging station
    });

    return {
      ...route,
      vehicles: filteredVehicles,
    };
  });

  return filteredRoutes;
};

export default function MobileStopScreen() {
  const { t } = useTranslation();
  const { organizationId } = useParams();
  const navigate = useNavigate();

  const [stops, setStops] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [routesWithVehicles, setRoutesWithVehicles] = useState([]);
  const [organizationDetails, setOrganizationDetails] = useState(null);
  const [liveData, setLiveData] = useState({}); // NEW: Track live vehicle locations
  const [loading, setLoading] = useState(true);
  const [originStop, setOriginStop] = useState(null);
  const [destinationStop, setDestinationStop] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentSelection, setCurrentSelection] = useState(null);
  const [tempSelectedStop, setTempSelectedStop] = useState(null);

  const [originError, setOriginError] = useState(false);
  const [destinationError, setDestinationError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const iconAdornmentStyles = { width: 20, height: 20 };
  const organizationInactive = isInactiveOrganization(organizationDetails);

  const routeStopSequences = useMemo(
    () => buildRouteStopSequences(routesWithVehicles, stops),
    [routesWithVehicles, stops],
  );

  // Add this useEffect after fetching routes
  useEffect(() => {
    if (organizationInactive) return;
    if (routesWithVehicles.length === 0) return;

    const deviceIds = [];
    routesWithVehicles.forEach(r => {
      (r.vehicles || [])
        .filter(v => v.status === "ACTIVE")
        .forEach(v => {
          const id = v.deviceId || v.device_id || (v.device && v.device.id) || v.id;
          if (id && !deviceIds.includes(id)) deviceIds.push(id);
        });
    });

    if (deviceIds.length === 0) return;

    fetchInitialLocations(deviceIds)
      .then(data => {
        setLiveData(data || {});
        console.log(`📍 Fetched live data for ${Object.keys(data || {}).length} vehicles`);
      })
      .catch(err => {
        console.error("Error fetching live locations:", err);
      });
  }, [routesWithVehicles, organizationInactive]);

  useEffect(() => {
    if (!organizationId) {
      console.error("Organization ID not found in URL");
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch Stops
        const stopsData = await getStopsByOrgIdIndependent(organizationId);
        setStops(stopsData || []);

        // Fetch Routes (with vehicles included via Prisma relation)
        const routesData = await getRoutesByOrgIdIndependent(organizationId);
        const fullRoutes = routesData || [];

        // Store full routes (with vehicles) for passing to BusList
        setRoutesWithVehicles(fullRoutes);

        // Flatten for backward compatibility (if needed elsewhere)
        const flatVehicles = fullRoutes.flatMap(
          (route) => route.vehicles || []
        );
        const flatRoutes = fullRoutes.map((route) => ({
          id: route.id,
          name: route.name,
          routeId: route.routeId,
        }));

        setVehicles(flatVehicles);
        setRoutes(flatRoutes);

        // Fetch organization details for SOS contact phone
        const orgDetails = await getOrganizationById(organizationId);
        setOrganizationDetails(orgDetails);

      } catch (error) {
        console.error("Error fetching organization data:", error);
        if (error.response?.status === 401) {
          navigate("/");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId, navigate]);

  // Helper functions for validation
  const normalizeStopName = (name) => {
    if (!name) return '';
    return name.trim().toLowerCase().replace(/\s+/g, ' ');
  };

  // Validate selection when origin or destination changes.
  useEffect(() => {
    if (!originStop || !destinationStop || routeStopSequences.length === 0) return;

    const shareRoute = routeStopSequences.some((route) => {
      const hasOrigin = route.stops.some((stop) => isSameStop(stop, originStop));
      const hasDestination = route.stops.some((stop) =>
        isSameStop(stop, destinationStop),
      );
      return hasOrigin && hasDestination;
    });

    if (!shareRoute) {
      setDestinationStop(null);
      return;
    }

    const checkValidity = () => routesWithVehicles.some(route =>
      routeAllowsJourney(route, originStop, destinationStop));

    if (!checkValidity()) {
      setDestinationStop(null);
    }
  }, [originStop, destinationStop, routeStopSequences]);

  const handleOpenDrawer = (selectionType) => {
    if (selectionType === "origin") {
      setOriginError(false);
    } else {
      setDestinationError(false);
    }
    setErrorMessage("");

    setCurrentSelection(selectionType);
    if (selectionType === "origin") {
      setTempSelectedStop(originStop);
    } else {
      setTempSelectedStop(destinationStop);
    }
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setCurrentSelection(null);
    setTempSelectedStop(null);
  };

  const handleStopSelect = (stop) => {
    setTempSelectedStop(stop);
  };

  const handleConfirmSelection = () => {
    if (!tempSelectedStop) return;

    if (currentSelection === "origin") {
      setOriginStop(tempSelectedStop);
      setOriginError(false);

      const relatedDestinations = getRelatedStopsForSelection(
        routeStopSequences,
        tempSelectedStop,
      );
      const shouldClearDestination =
        isSameStop(destinationStop, tempSelectedStop) ||
        (destinationStop &&
          relatedDestinations.length > 0 &&
          !relatedDestinations.some((stop) => isSameStop(stop, destinationStop)));

      if (shouldClearDestination) {
        setDestinationStop(null);
      }
    } else if (currentSelection === "destination") {
      setDestinationStop(tempSelectedStop);
      setDestinationError(false);

      const relatedOrigins = getRelatedStopsForSelection(
        routeStopSequences,
        tempSelectedStop,
      );
      const shouldClearOrigin =
        isSameStop(originStop, tempSelectedStop) ||
        (originStop &&
          relatedOrigins.length > 0 &&
          !relatedOrigins.some((stop) => isSameStop(stop, originStop)));

      if (shouldClearOrigin) {
        setOriginStop(null);
      }
    }
    setErrorMessage("");
    handleCloseDrawer();
  };

  const handleSwapLocations = () => {
    if (originStop && destinationStop) {
      const temp = originStop;
      setOriginStop(destinationStop);
      setDestinationStop(temp);
      setOriginError(false);
      setDestinationError(false);
      setErrorMessage("");
    }
  };

  const handleViewRoutes = () => {
    if (organizationInactive) return;

    // Validate both fields
    const hasOrigin = !!originStop;
    const hasDestination = !!destinationStop;

    if (!hasOrigin || !hasDestination) {
      setOriginError(!hasOrigin);
      setDestinationError(!hasDestination);

      if (!hasOrigin && !hasDestination) {
        setErrorMessage(t("Please select both origin and destination stops"));
      } else if (!hasOrigin) {
        setErrorMessage(t("Please select origin stop"));
      } else {
        setErrorMessage(t("Please select destination stop"));
      }
      return;
    }

    // Clear errors
    setOriginError(false);
    setDestinationError(false);
    setErrorMessage("");

    // Prepare charging stations list to pass
    const chargingStations = stops
      .filter(stop => isChargingStation(stop.name))
      .map(stop => ({
        name: stop.name,
        latitude: stop.latitude || stop.lat,
        longitude: stop.longitude || stop.lng,
      }))
      .filter(station => station.latitude && station.longitude);

    // Add hardcoded charging station
    chargingStations.push({
        name: "Charging Station (Fixed)",
        latitude: CHARGING_STATION_COORDS.lat,
        longitude: CHARGING_STATION_COORDS.lng
    });

    navigate("/mobile/stop/bus", {
      state: {
        originStop,
        destinationStop,
        organizationId,
        routesWithVehicles, // BusList filters availability using refreshed data
        vehicles,
        routes,
        chargingStations, // PASS to BusList
        organization: organizationDetails,
      },
    });
  };

  const getAvailableStops = () => {
    let list = stops;
    const hasRouteSequences = routeStopSequences.length > 0;
    let preserveRouteOrder = false;

    if (currentSelection === "destination" && originStop) {
      const relatedStops = getRelatedStopsForSelection(
        routeStopSequences,
        originStop,
      );
      list = hasRouteSequences
        ? relatedStops
        : stops.filter((stop) => !isSameStop(stop, originStop));
      preserveRouteOrder = hasRouteSequences;
    } else if (currentSelection === "origin" && destinationStop) {
      const relatedStops = getRelatedStopsForSelection(
        routeStopSequences,
        destinationStop,
      );
      list = hasRouteSequences
        ? relatedStops
        : stops.filter((stop) => !isSameStop(stop, destinationStop));
      preserveRouteOrder = hasRouteSequences;
    }

    if (routesWithVehicles.length > 0) {
      list = list.filter(stop => {
        const origin = currentSelection === "origin" ? stop : originStop;
        const destination = currentSelection === "destination" ? stop : destinationStop;
        return !origin || !destination || routesWithVehicles.some(route =>
          routeAllowsJourney(route, origin, destination));
      });
    }

    const toNum = (val) => {
      if (val === undefined || val === null) return NaN;
      if (typeof val === "number") return val;
      const parsed = parseFloat(val);
      return Number.isFinite(parsed) ? parsed : NaN;
    };

    const availableStops = list
      .filter((s) => !isChargingStation(s.name))
      .slice();

    if (preserveRouteOrder) {
      return availableStops;
    }

    return availableStops
      .sort((a, b) => {
        const aLat = toNum(a.latitude ?? a.lat);
        const bLat = toNum(b.latitude ?? b.lat);
        if (!Number.isNaN(aLat) && !Number.isNaN(bLat) && aLat !== bLat) {
          return aLat - bLat;
        }
        const aLng = toNum(a.longitude ?? a.lng);
        const bLng = toNum(b.longitude ?? b.lng);
        if (!Number.isNaN(aLng) && !Number.isNaN(bLng)) {
          return aLng - bLng;
        }
        return (a.name || "").localeCompare(b.name || "");
      });
  };

  return (
    <>
      <Box className="mobile-screen-wrapper">
        <Box className="mobile-stop-header">
          <img
            src={Org_Logo}
            alt="RouteEye Logo"
            className="mobile-stop-logo"
          />
        </Box>

        <Box className="mobile-stop-container">
          <Typography
            className="mobile-stop-title"
            sx={{
              fontWeight: 600,
              color: "#183059",
              zIndex: 1,
              fontSize: "28px",
              textAlign: "left",
              fontFamily: "Roboto",
            }}
          >
            {t("Employee Bus Service")}
          </Typography>
        </Box>

        <Box className="text-section">
          <Typography variant="body1" className="mobile-stop-subtext">
            {t(
              "Dedicated shuttle for staff to travel smoothly across the airport."
            )}
          </Typography>

          <Box className="info-box">
            <img src={Info_Banner} alt="info banner" />
            <Typography className="info-text">
              {t(
                "Shuttle service halts for few minutes between the trips and shuttle may stop at charging station for few hours"
              )}
            </Typography>
          </Box>
        </Box>

        <Box className="input-section">
          <TextField
            placeholder={originStop ? "" : t("Leaving from")}
            variant="outlined"
            fullWidth
            className="textfield"
            value={originStop?.name || ""}
            onClick={() => handleOpenDrawer("origin")}
            error={originError}
            helperText={originError && !destinationError ? errorMessage : ""}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <img
                    src={LeavingStartIcon}
                    alt="leaving icon"
                    style={iconAdornmentStyles}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <img
                    src={Arrow_Down}
                    alt="dropdown arrow"
                    style={iconAdornmentStyles}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              cursor: "pointer",
              "& .MuiOutlinedInput-root": {
                cursor: "pointer",
                "&.Mui-error": {
                  "& fieldset": {
                    borderColor: "#d32f2f",
                    borderWidth: "2px",
                  },
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#C8CBD0",
                opacity: 1,
              },
            }}
          />

          <Box className="change-btn-wrapper">
            <Button
              className="change-btn"
              aria-label="swap locations"
              onClick={handleSwapLocations}
              disabled={!originStop || !destinationStop}
            >
              <img src={Change_Icon} alt="change" />
            </Button>
          </Box>

          <TextField
            placeholder={destinationStop ? "" : t("Going to")}
            variant="outlined"
            fullWidth
            className="textfield"
            value={destinationStop?.name || ""}
            onClick={() => handleOpenDrawer("destination")}
            error={destinationError}
            helperText={destinationError && !originError ? errorMessage : ""}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <img
                    src={GoingStartIcon}
                    alt="destination icon"
                    style={iconAdornmentStyles}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <img
                    src={Arrow_Down}
                    alt="dropdown arrow"
                    style={iconAdornmentStyles}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              cursor: "pointer",
              "& .MuiOutlinedInput-root": {
                cursor: "pointer",
                "&.Mui-error": {
                  "& fieldset": {
                    borderColor: "#d32f2f",
                    borderWidth: "2px",
                  },
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#C8CBD0",
                opacity: 1,
              },
            }}
          />

          {originError && destinationError && errorMessage && (
            <Typography
              sx={{
                color: "#d32f2f",
                fontSize: "0.75rem",
                marginTop: 1,
                marginLeft: 2,
              }}
            >
              {errorMessage}
            </Typography>
          )}

          <Button
            variant="contained"
            className="find-btn"
            fullWidth
            sx={{ marginTop: 3 }}
            onClick={handleViewRoutes}
            disabled={organizationInactive}
          >
            {t("View Routes")}
          </Button>
          {organizationInactive && (
            <Typography className="inactive-org-message">
              {t(
                "organizationInactiveTrackingMessage",
                "This organization is inactive. Contact the RouteEye admin to activate it and restore.",
              )}
            </Typography>
          )}
        </Box>
      </Box>

      <img
        src={Bus_Background}
        alt="Bus illustration"
        className="bus-background"
      />

      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            height: "60vh",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            padding: 2,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
              paddingBottom: 1,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#111214" }}>
              {currentSelection === "origin"
                ? t("Where are you leaving from")
                : t("Where are you going to")}
            </Typography>
            <IconButton
              onClick={handleCloseDrawer}
              size="small"
              sx={{
                backgroundColor: "#E8EAEE",
                "&:hover": {
                  backgroundColor: "#d5d7db",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 4,
                flex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <List
                sx={{
                  overflow: "auto",
                  flex: 1,
                  padding: 0,
                  marginBottom: 2,
                }}
              >
                {getAvailableStops().length > 0 ? (
                  getAvailableStops().map((stop) => (
                    <ListItem
                      key={stop.id}
                      button
                      onClick={() => handleStopSelect(stop)}
                      sx={{
                        borderBottom: "1px solid #e0e0e0",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                        "&:last-child": {
                          borderBottom: "none",
                        },
                        padding: "12px 16px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Radio
                        checked={tempSelectedStop?.id === stop.id}
                        onChange={() => handleStopSelect(stop)}
                        sx={{
                          color: "#B7BFCC",
                          padding: 0,
                          marginRight: 2,
                          "&.Mui-checked": {
                            color: "#20A8E0",
                          },
                        }}
                      />
                      <ListItemText
                        primary={stop.name}
                        primaryTypographyProps={{
                          fontFamily: "Roboto",
                          fontWeight: 400,
                          fontSize: "16px",
                          lineHeight: "100%",
                          color: "#1F242E",
                        }}
                      />
                    </ListItem>
                  ))
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      padding: 4,
                    }}
                  >
                    <Typography color="text.secondary">
                      {t("No stops available")}
                    </Typography>
                  </Box>
                )}
              </List>

              <Box sx={{ padding: "0 16px 16px 16px" }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleConfirmSelection}
                  disabled={!tempSelectedStop}
                  sx={{
                    backgroundColor: "#F9E719",
                    color: "#111214",
                    height: "39px",
                    borderRadius: "25px",
                    padding: "10px 16px",
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "100%",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#e8d617",
                      boxShadow: "none",
                    },
                    "&:disabled": {
                      backgroundColor: "#f5f5f5",
                      color: "#9e9e9e",
                    },
                  }}
                >
                  {t("Confirm")}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
}
